'use client'

import { FilterBarMobile } from '@/app/collection/_components/filter-bar/filter-bar-mobile'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { TCollectionFormatted } from '@/types/collection'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { COLLECTION_SORT_LABELS, ECollectionSort } from '../../constants'
import { useCollectionStore } from '../../store/collection-store'
import {
  applyFilter,
  FilterTag,
  getFilterTags,
  objectToSearchParams,
} from '../../utils/filter-tag-utils'
import { getFilterStatus, updateUrl } from '../../utils/filter-utils'
import { FilterBarTags } from '../filter-bar/filter-bar-tags'
import { processSeoRedirectRemoveTag } from '../filter-bar/use-filter-state'

export const FilterSummaryBarMobile = ({
  collection,
  className,
}: {
  collection: TCollectionFormatted
  className?: string
}) => {
  const productCount = useCollectionStore((s) => s.productCount)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const setFilter = useCollectionStore((s) => s.setFilter)
  const filter = useCollectionStore((s) => s.filter)

  const onClearFilter = () => {
    const currentParams = objectToSearchParams(filter)
    const newUrl = processSeoRedirectRemoveTag(
      pathname,
      collection,
      new URLSearchParams(),
      currentParams
    )
    const newPath = newUrl.split('?')[0]

    if (window.location.pathname + window.location.search !== newUrl) {
      if (newPath !== window.location.pathname) {
        router.push(newUrl, { scroll: false })
      } else {
        updateUrl(newUrl, { scroll: false })
      }
    }
    setFilter({})
  }

  const handleSortChange = (value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('sort', value)
    updateUrl(`${pathname}?${params.toString()}`, { scroll: false })
    setFilter({ sort: value })
  }

  const tags = getFilterTags(filter, collection)

  const onRemoveTag = (tag: FilterTag) => {
    const newFilters = applyFilter(filter, tag)
    const newParams = objectToSearchParams(newFilters)
    const currentParams = objectToSearchParams(filter)
    setFilter(newFilters)

    const newUrl = processSeoRedirectRemoveTag(
      pathname,
      collection,
      newParams,
      currentParams
    )

    const newPath = newUrl.split('?')[0]

    if (window.location.pathname + window.location.search !== newUrl) {
      if (newPath !== window.location.pathname) {
        router.push(newUrl, { scroll: false })
      } else {
        updateUrl(newUrl, { scroll: false })
      }
    }
  }

  const { hasFilter } = getFilterStatus(filter, collection)

  return (
    <div
      className={cn(
        'sticky top-0 z-50 bg-light px-5 pb-4 pt-3.5 shadow-[0_4px_12px_0_rgba(0,0,0,0.08)]',
        className
      )}
    >
      <div className='flex items-center justify-between'>
        <p className='font-sans text-sm leading-4.5 text-neutral-500'>
          {productCount || 0} kết quả
        </p>

        <div className='flex items-center gap-2'>
          <Select
            options={Object.entries(COLLECTION_SORT_LABELS).map(
              ([key, label]) => ({
                label,
                value: key,
              })
            )}
            onValueChange={handleSortChange}
            defaultValue={searchParams.get('sort') || ECollectionSort.NEW}
            className={{
              trigger: 'min-w-[106px] border-neutral-300 px-3 py-2.5',
            }}
          />
          <FilterBarMobile collection={collection} />
        </div>
      </div>

      {hasFilter && (
        <div className='mt-2.5 flex items-center gap-3 border-t border-t-neutral-200 pt-2'>
          <button
            className='p-2 font-sans text-sm font-semibold leading-4.5 text-primary/90'
            onClick={onClearFilter}
          >
            Xoá lọc
          </button>{' '}
          <FilterBarTags
            tags={tags}
            onRemoveTag={onRemoveTag}
            className='flex-1 flex-nowrap overflow-auto border-0 pb-0'
          />
        </div>
      )}
    </div>
  )
}
